/**
 * filter_bar — the Alpine component behind every chip row / search box /
 * dropdown that swaps a section over HTMX.
 *
 * Markup contract (rendered by the filter_chip* and inline_* macros in
 * _components.html): the x-data root carries data-section, data-endpoint,
 * optional data-param-prefix, data-deep-link-keys and data-default-<key>;
 * chips carry data-chip-group/data-chip-value or data-chip-all-for.
 */
import { listen, unlisten_all } from "./helpers.js";

// Dropdown fields are flat properties because the CSP Alpine build only
// accepts a bare identifier in x-model.
const DROPDOWN_KEYS = ["environment", "persona", "project_id", "provenance", "actor_id", "range"];

// The data-default-{key} attribute convention maps a snake_case key (as
// written in chip/dropdown markup) to the camelCase dataset property the
// DOM exposes it under, e.g. "target_type" -> el.dataset.defaultTargetType.
function datasetDefaultFor(el, key) {
  const camel = key.replace(/_([a-z])/g, (_, c) => c.toUpperCase());
  const dsKey = `default${camel.charAt(0).toUpperCase()}${camel.slice(1)}`;
  return el.dataset[dsKey] || "";
}

document.addEventListener("alpine:init", () => {
  Alpine.data("filter_bar", () => ({
    _listeners: [],
    search: "",
    chips: {},
    environment: "",
    persona: "",
    range: "",
    project_id: "",
    provenance: "",
    actor_id: "",
    _root: null,
    _section: "",
    _endpoint: "",
    _paramPrefix: "",
    _chipKeys: [],
    _keepPage: false,
    init() {
      this._root = this.$el;
      this._section = this._root.dataset.section || "";
      this._endpoint = this._root.dataset.endpoint || "";
      this._paramPrefix = this._root.dataset.paramPrefix || "";
      const params = new URLSearchParams(window.location.search);
      const prefix = this._paramPrefix;
      this.search = params.get(`${prefix}q`) || "";
      // Chip groups are discovered from the markup rather than hard-coded,
      // so a template never has to register a new key here. "page" stays
      // because setPage() writes it even though no chip button carries it.
      this._chipKeys = ["page"];
      const chipEls = this._root.querySelectorAll("[data-chip-group], [data-chip-all-for]");
      for (let c = 0; c < chipEls.length; c++) {
        const g = chipEls[c].dataset.chipGroup || chipEls[c].dataset.chipAllFor;
        if (this._chipKeys.indexOf(g) === -1) this._chipKeys.push(g);
      }
      // data-deep-link-keys (comma-separated) opts specific keys into a
      // second fallback: if the prefixed param is missing, also check the
      // un-prefixed form. Routes that accept canonical query params (e.g.
      // /dashboard?trigger=scheduled) can use this so a shared deep link
      // still lights the matching chip on arrival.
      const deepLinkRaw = this._root.dataset.deepLinkKeys || "";
      const deepLinkKeys = deepLinkRaw
        ? deepLinkRaw
            .split(",")
            .map((s) => s.trim())
            .filter(Boolean)
        : [];
      // Restore chip state from URL, falling back to data-default-{key}
      // attributes when the URL omits the param. Lets the server pre-select
      // a chip on first render without dirtying the URL.
      for (let i = 0; i < this._chipKeys.length; i++) {
        const key = this._chipKeys[i];
        let val = params.get(prefix + key);
        if (!val && prefix && deepLinkKeys.indexOf(key) !== -1) {
          val = params.get(key) || "";
        }
        if (!val) val = datasetDefaultFor(this._root, key);
        if (val) this.chips[key] = val;
      }
      // Restore dropdown state, with the same data-default-{key} fallback
      // as chips (used by the Test Health range selector so the dropdown
      // matches the server-rendered chart on first load).
      for (let d = 0; d < DROPDOWN_KEYS.length; d++) {
        const dk = DROPDOWN_KEYS[d];
        this[dk] = params.get(prefix + dk) || datasetDefaultFor(this._root, dk);
      }
      // Apply active chip styles from restored state
      this.$nextTick(() => {
        this._syncChipPressed();
      });
      // Listen for HTMX errors on our target section
      this._setupErrorHandler();
    },
    _setupErrorHandler() {
      const sectionId = this._section;
      if (!sectionId) return;
      listen(this, document.body, "htmx:responseError", (evt) => {
        if (evt.detail.target && evt.detail.target.id === sectionId) {
          const target = document.getElementById(sectionId);
          if (target && !target.querySelector('[role="alert"]')) {
            const banner = document.createElement("div");
            banner.className =
              "rounded-md bg-red-50 border border-red-200 p-3 mb-3 text-sm text-red-700";
            banner.setAttribute("role", "alert");
            banner.textContent = "Failed to load results. Please try again.";
            target.prepend(banner);
            target.classList.remove("opacity-50", "pointer-events-none");
            target.removeAttribute("aria-busy");
          }
        }
      });
    },
    destroy() {
      unlisten_all(this);
    },
    toggleChip(group, value) {
      delete this.chips.page;
      const current = this.chips[group] || "";
      const parts = current ? current.split(",") : [];
      const idx = parts.indexOf(value);
      if (idx >= 0) {
        parts.splice(idx, 1);
      } else {
        parts.push(value);
      }
      this.chips[group] = parts.join(",");
      this._syncChipPressed();
      this.apply();
    },
    setSingleChip(group, value) {
      delete this.chips.page;
      this.chips[group] = value;
      this._syncChipPressed();
      this.apply();
    },
    isActive(group, value) {
      const current = this.chips[group] || "";
      if (!current) return false;
      return current.split(",").indexOf(value) >= 0;
    },
    isGroupEmpty(group) {
      return !this.chips[group];
    },
    clearChipGroup(group) {
      delete this.chips.page;
      this.chips[group] = "";
      this._syncChipPressed();
      this.apply();
    },
    _syncChipPressed() {
      const buttons = this._root.querySelectorAll("[data-chip-group]");
      for (let i = 0; i < buttons.length; i++) {
        const btn = buttons[i];
        const active = this.isActive(btn.dataset.chipGroup, btn.dataset.chipValue);
        btn.setAttribute("aria-pressed", String(active));
      }
      const allBtns = this._root.querySelectorAll("[data-chip-all-for]");
      for (let j = 0; j < allBtns.length; j++) {
        const allBtn = allBtns[j];
        const allActive = this.isGroupEmpty(allBtn.dataset.chipAllFor);
        allBtn.setAttribute("aria-pressed", String(allActive));
      }
    },
    setPage(p) {
      this.chips.page = String(p);
      this._keepPage = true;
      this.apply();
    },
    // Every param this bar owns: the search box, every chip group found in
    // the markup, and every dropdown. Shared between the browser-URL write
    // and the HTMX request params in apply() so the two stay in lockstep.
    // Every chip group in `this.chips` also appears in `_chipKeys` (both come
    // from the same data-chip-* markup), so apply() can delete exactly the
    // keys this bar may have set.
    _ownState() {
      const state = {};
      if (this.search) state.q = this.search;
      for (const key in this.chips) {
        if (this.chips[key]) state[key] = this.chips[key];
      }
      for (let i = 0; i < DROPDOWN_KEYS.length; i++) {
        const dk = DROPDOWN_KEYS[i];
        if (this[dk]) state[dk] = this[dk];
      }
      return state;
    },
    apply() {
      // Clear prior error banners
      const bannerTarget = document.getElementById(this._section);
      if (bannerTarget) {
        const banners = bannerTarget.querySelectorAll('[role="alert"]');
        for (let i = 0; i < banners.length; i++) {
          banners[i].remove();
        }
      }
      // Reset page on filter/search changes (but not pagination)
      if (!this._keepPage) {
        delete this.chips.page;
      }
      this._keepPage = false;

      const prefix = this._paramPrefix;
      const state = this._ownState();
      // Preserve URL params from other filter bars
      const params = new URLSearchParams(window.location.search);
      const ownKeys = ["q"].concat(this._chipKeys, DROPDOWN_KEYS);
      for (let i = 0; i < ownKeys.length; i++) {
        params.delete(prefix + ownKeys[i]);
      }
      // Add back our current state
      for (const key in state) {
        params.set(prefix + key, state[key]);
      }

      const qs = params.toString();
      // Build HTMX URL with only this bar's params (endpoint expects unprefixed)
      const htmxQs = new URLSearchParams(state).toString();
      const url = this._endpoint + (htmxQs ? `?${htmxQs}` : "");
      // Update browser URL for bookmarkability
      const fullQs = qs ? `?${qs}` : window.location.pathname;
      history.replaceState(null, "", fullQs);
      // Trigger HTMX request with loading state
      const target = document.getElementById(this._section);
      if (target) {
        target.classList.add("opacity-50", "pointer-events-none");
        target.setAttribute("aria-busy", "true");
        htmx
          .ajax("GET", url, { target: target, swap: "innerHTML", source: this._root })
          .then(() => {
            target.classList.remove("opacity-50", "pointer-events-none");
            target.removeAttribute("aria-busy");
          })
          .catch(() => {
            target.classList.remove("opacity-50", "pointer-events-none");
            target.removeAttribute("aria-busy");
          });
      }
    },
  }));
});
